/**
 * Cross-cutting registry of live ECharts instances on the dashboard.
 *
 * Each chart widget registers `(id, getInstance)` on mount and
 * unregisters on unmount. The whole-dashboard PDF export reads the
 * registry to snapshot every chart at once without prop-drilling
 * instances up to App.tsx.
 *
 * Storage is delegated to {@link createRegistry} (R-18); only the
 * entry shape and the React hook stay local to this module.
 */
import { useEffect } from 'react';
import type * as echarts from 'echarts/core';
import { createRegistry } from './create-registry';

export type ChartGetInstance = () => echarts.ECharts | null;

export type ChartRegistryEntry = {
  id: string;
  title: string;
  getInstance: ChartGetInstance;
};

const registry = createRegistry<ChartRegistryEntry>();

/** Register a chart. Returns an unregister function for symmetry with hooks. */
export function registerChart(entry: ChartRegistryEntry): () => void {
  return registry.register(entry);
}

/** Snapshot the current registry as a stable list (insertion order). */
export function listCharts(): ChartRegistryEntry[] {
  return registry.list();
}

/** Visible for testing. */
export function _resetRegistryForTests(): void {
  registry.reset();
}

/**
 * React hook that keeps a chart registered with the dashboard
 * registry for as long as the component is mounted. Re-registers
 * when `id` or `title` change so the PDF cover page reflects the
 * current widget title. `getInstance` is expected to be a stable
 * callback (the one returned by `useEChartsInstance`); if not, the
 * effect dep array will churn it.
 */
export function useChartRegistration(
  id: string,
  title: string,
  getInstance: ChartGetInstance,
): void {
  useEffect(() => {
    return registerChart({ id, title, getInstance });
  }, [id, title, getInstance]);
}
